// Story 008 AC7–AC10 — D-5 role/phase capability gate.
//
// Source of truth: `.vibe/62_LYSE_STORIES_FROM_MEETING.md` §6 D-5
// (canonical) + `.vibe/63_DA_ASSIGNEE_ASSIGNMENT.md`. The matrix is
// reproduced in story file `docs/stories/shipped/lyse/008-defect-editing.md`
// AC7–AC10 with the exact tooltip copy.
//
// Predicate:
//   writer-eligible when
//     (evaluationState === "PRE_SCREENING"   && me.id === da.prescreenerUserId) OR
//     (evaluationState === "EXPERT_ANALYSIS" && me.id === da.expertUserId)
//
// Phase-frozen states (EVALUATION_DONE / LOCKED / REJECTED) and the
// implicit `NOT_STARTED` are all read-only — regardless of role.
//
// Back-compat: when BOTH assignee fields are null we treat the DA as
// "legacy" (Phase-1 BE hasn't backfilled yet) and keep the current
// pre-D-5 behaviour — editors enabled IFF the phase is not frozen.
// SB6-G2 Bug-4 fix: the gateway in production still emits both
// assignee keys as `null` (see lyse-analysis.service.ts §"5.2.0
// additive — PR-A surfaces the keys as null"), so the legacy
// back-compat branch was returning `allowed: true` even for LOCKED
// data acquisitions — which let the Draw new defect toolbar render on
// a frozen DA. The PHASE_FROZEN guard must run BEFORE the legacy
// shortcut.

import type { DataAcquisitionState } from "@aeriosiq/types";

export type DefectEditDenyReason =
  | { code: "phase"; stateLabel: DataAcquisitionState }
  | { code: "role"; phase: "PRE_SCREENING" | "EXPERT_ANALYSIS" };

export type DefectEditPermission =
  | { allowed: true }
  | { allowed: false; reason: DefectEditDenyReason };

const PHASE_FROZEN: ReadonlySet<DataAcquisitionState> = new Set([
  "EVALUATION_DONE",
  "LOCKED",
  "REJECTED",
]);

interface ResolveArgs {
  evaluationState: DataAcquisitionState | null;
  prescreenerUserId: string | null;
  expertUserId: string | null;
  meId: string | null | undefined;
}

/**
 * Resolve whether the current user may inline-edit a defect on this DA.
 * Returns a discriminated union so the UI can pick the right tooltip
 * copy without re-deriving the reason.
 */
export function resolveDefectEditPermission(
  args: ResolveArgs,
): DefectEditPermission {
  const { evaluationState, prescreenerUserId, expertUserId, meId } = args;

  // SB6-G2 Bug-4 — PHASE_FROZEN check FIRST so a LOCKED data acquisition
  // never falls into the legacy back-compat shortcut below. Without this
  // ordering the back-compat branch wins on every live BE response (the
  // gateway still emits `prescreenerUserId: null, expertUserId: null`
  // pending PR-B/PR-E), and the Draw new defect toolbar — gated on this
  // function — renders even when the underlying DA is LOCKED.
  if (evaluationState && PHASE_FROZEN.has(evaluationState)) {
    return {
      allowed: false,
      reason: { code: "phase", stateLabel: evaluationState },
    };
  }

  // Legacy back-compat — see file header. Both assignee slots null means
  // the BE hasn't started populating the columns yet; behave like
  // pre-D-5 and allow editing. Only kicks in for non-frozen phases now.
  if (prescreenerUserId === null && expertUserId === null) {
    return { allowed: true };
  }

  if (evaluationState === "PRE_SCREENING") {
    if (meId && prescreenerUserId === meId) {
      return { allowed: true };
    }
    return {
      allowed: false,
      reason: { code: "role", phase: "PRE_SCREENING" },
    };
  }

  if (evaluationState === "EXPERT_ANALYSIS") {
    if (meId && expertUserId === meId) {
      return { allowed: true };
    }
    return {
      allowed: false,
      reason: { code: "role", phase: "EXPERT_ANALYSIS" },
    };
  }

  // CREATED or null (no analysis phase yet). Treat as frozen with a
  // phase reason — there is no assigned writer until pre-screening
  // begins. SB3 has no NOT_STARTED; CREATED is the pre-workflow placeholder.
  return {
    allowed: false,
    reason: { code: "phase", stateLabel: evaluationState ?? "CREATED" },
  };
}

/**
 * Tooltip copy locked in `docs/stories/shipped/lyse/008-defect-editing.md`
 * AC9 / AC10. Keep these strings verbatim — the acceptance tests
 * regex-match them.
 */
export function denyReasonTooltip(reason: DefectEditDenyReason): string {
  if (reason.code === "phase") {
    return `Edits are frozen — data acquisition is in ${reason.stateLabel}.`;
  }
  if (reason.phase === "PRE_SCREENING") {
    return "Editing during Pre-Screening is restricted to the assigned Pre-Screener.";
  }
  return "Editing during Expert Analysis is restricted to the assigned Expert Analyst.";
}
